import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { useAttachmentsService } from '../attachments.service';
import { AttachmentType } from '../attachments.store';
import { AttachmentAddFolder } from '../components/AttachmentAddFolder';

export const AttachmentAddFolderContainer = observer((): ReactElement => {
  const attachmentsService = useAttachmentsService();

  const { attachmentId } = useParams<{ attachmentId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderNameStatus, setFolderNameStatus] = useState(InputStatus.empty());

  const { isLoading, withLoaderAsync } = useLoader();
  const handleFolderNameChange = (folderName: string): void => {
    const trimmed = folderName.trim();
    setFolderName(folderName);

    if (!trimmed) {
      setFolderNameStatus(InputStatus.buildFrom(['NAME_CAN_NOT_BE_EMPTY']));
      return;
    }

    const folderNames = attachmentsService.store.attachments.filter((a) => a.type === AttachmentType.FOLDER).map((a) => a.name.toLocaleUpperCase());
    const nameTaken = folderNames.includes(trimmed.toLocaleUpperCase());
    setFolderNameStatus(nameTaken ? InputStatus.buildFrom(['DIRECTORY_ALREADY_EXISTS']) : InputStatus.empty());
  };

  const handleAddFolder = async (): Promise<void> => {
    const trimmed = folderName.trim();

    await withLoaderAsync(async () => {
      const result = await attachmentsService.addAttachmentFolderAsync(trimmed, attachmentId);

      if (result instanceof ACDLError) {
        setFolderNameStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? []));
      } else {
        setIsModalOpen(false);
        setFolderName('');
      }
    });
  };

  const toggleModal = (): void => {
    setIsModalOpen((prev) => !prev);
    setFolderName('');
    setFolderNameStatus(InputStatus.empty());
  };

  return (
    <AttachmentAddFolder
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      folderName={folderName}
      toggleModal={toggleModal}
      onFolderNameChange={handleFolderNameChange}
      onAddFolderConfirmClicked={handleAddFolder}
      folderNameStatus={folderNameStatus}
    />
  );
});
